import React, { useState, useEffect } from "react";
import {
  FormSubElementTypes,
  BaseFormContentElement,
  TextFieldElement,
  NumberFieldElement,
  EmailFieldElement,
  PhoneNumberFieldElement,
  DropdownSelectElement,
  WorkyCalendarElement,
  FormContentElement,
  FormDefinitionStep,
  FormDefinition,
  SubmitButtonDefinition,
  BooleanOperatorElement,
  TextAreaElement,
  HashMapElement,
  DynamicListElement,
  EmptyPlaceholderElement
} from "./FormSharedInterfaces";

import {
  EmailFieldElementComponent,
  EnhancedFormSubmitButtonComponent,
  FormBackButtonComponent,
  FormCancelButtonComponent,
  FormSubmitButtonComponent,
  NumberFieldElementComponent,
  DropdownSelectElementComponent,
  TextFieldElementComponent,
  useFormValidation,
  BooleanOperatorElementComponent,
  PhoneNumberFieldElementComponent,
  TextAreaElementComponent,
  HashMapElementComponent,
  DynamicListElementComponent,
  EmptyPlaceholderElementComponent
} from "./FormGenerationComponents";

// Define RenderFormStepContent outside of FormGenerationService
const RenderFormStepContent = ({
  stepIndex,
  step,
  submitButtonDefinition,
}: {
  stepIndex: number;
  step: FormDefinitionStep;
  submitButtonDefinition: SubmitButtonDefinition;
}) => {
  const newErrors: Record<string, boolean> = {};
  const { isStepValid, updateFieldValidity } = useFormValidation(step);
  
  if (!step) {
    console.warn("stepIndex: " + stepIndex + "  step in FormGenerationService is empty");
    return <div></div>;
  }

  return (
    <div>
      <div key={`child-step-${stepIndex}`} className="form-step">
        <h3>{step.formStepName}</h3>
        {step.formStepContents.map((element, elementIndex) => {
          // Use a more stable key if possible, e.g., element.id if it exists and is unique
          const key = element.elementUniqueName || `${stepIndex}-${elementIndex}`; // Example: Prefer element.id
          console.log("key ", key);
          switch (element.formSubElementTypes) {
            case FormSubElementTypes.NUMBER_FIELD:
              return (
                <div key={`child-step-key-${key}`} className="form-element-placeholder">
                  <NumberFieldElementComponent elementDetails={element as NumberFieldElement} />
                  <br />
                </div>
              );

            case FormSubElementTypes.TEXT_FIELD:
              return (
                <div key={`child-step-key-${key}`} className="form-element-placeholder">
                  <TextFieldElementComponent elementDetails={element as TextFieldElement} />
                  <br />
                </div>
              );
            case FormSubElementTypes.EMAIL_FIELD:
              return (
                <div key={`child-step-key-${key}`} className="form-element-placeholder">
                  <EmailFieldElementComponent elementDetails={{...element as EmailFieldElement, updateFieldValidity}} />
                  <br />
                </div>
              );
            case FormSubElementTypes.DROP_DOWN_SELECT:
            return (
            <div key={`child-step-key-${key}`} className="form-element-placeholder">
                <DropdownSelectElementComponent elementDetails={{...element as DropdownSelectElement, updateFieldValidity}} />
                <br />
            </div>
            );
            case FormSubElementTypes.BOOLEAN_OPERATOR:
            return (
            <div key={`child-step-key-${key}`} className="form-element-placeholder">
                <BooleanOperatorElementComponent elementDetails={{...element as BooleanOperatorElement, updateFieldValidity}} />
                <br />
            </div>
            );
            case FormSubElementTypes.PHONE_NUMBER_FIELD:
            return (
            <div key={`child-step-key-${key}`} className="form-element-placeholder">
                <PhoneNumberFieldElementComponent elementDetails={{...element as PhoneNumberFieldElement, updateFieldValidity}} />
                <br />
            </div>
            );
            case FormSubElementTypes.TEXT_AREA:
            return (
            <div key={`child-step-key-${key}`} className="form-element-placeholder">
                <TextAreaElementComponent elementDetails={{...element as TextAreaElement, updateFieldValidity}} />
                <br />
            </div>
            );
            case FormSubElementTypes.HASH_MAP:
              return (
                <div key={`child-step-key-${key}`} className="form-element-placeholder">
                  <HashMapElementComponent elementDetails={{...element as HashMapElement, updateFieldValidity}} />
                  <br />
                </div>
              );
            case FormSubElementTypes.DYNAMIC_LIST:
              return (
                <div key={`child-step-key-${key}`} className="form-element-placeholder">
                  <DynamicListElementComponent elementDetails={{...element as DynamicListElement, updateFieldValidity}} />
                  <br />
                </div>
              );
            case FormSubElementTypes.EMPTY_PLACEHOLDER:
              return (
                <div key={`child-step-key-${key}`} className="form-element-placeholder">
                  <EmptyPlaceholderElementComponent elementDetails={{...element as EmptyPlaceholderElement, updateFieldValidity}} />
                  <br />
                </div>
              );
            default:
              return (
                <div key={`child-step-key-${key}`} className="form-element-placeholder">
                  <label>{element.elementHeaderMessage}</label>
                  <div>Element type {element.formSubElementTypes} not implemented yet</div>
                  <br />
                </div>
              );
          }
        })}
      </div>
      {/* Wrap buttons in a div with flex display and gap for spacing */}
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px' }}>
      <div style={{ display: 'flex', gap: '10px' }}>
        <FormBackButtonComponent submitButtonDefinition={submitButtonDefinition} />
        <FormCancelButtonComponent submitButtonDefinition={submitButtonDefinition} />
      </div>
      {/* <FormSubmitButtonComponent submitButtonDefinition={submitButtonDefinition} /> */}
      <EnhancedFormSubmitButtonComponent submitButtonDefinition={submitButtonDefinition} isStepValid={isStepValid}/>
    </div>
    </div>
  );
};

const FormGenerationService = ({ 
  formDefinition, 
  onStepChange 
}: { 
  formDefinition: FormDefinition;
  onStepChange?: (step: number) => void;
}) => {
  console.log("re-render of FormGenerationService ", formDefinition); // Log moved here
  const [currentStepValue, setCurrentStepValueStep] = useState(1);
  
  const nextStep = () => {
    // console.log("moving from current step ", currentStepValue, " to next step ", currentStepValue + 1);
    const newStep = currentStepValue + 1;
    setCurrentStepValueStep(newStep);
    if (onStepChange) {
      onStepChange(newStep);
    }
  };
  
  const prevStep = () => {
    const newStep = currentStepValue - 1;
    setCurrentStepValueStep(newStep);
    if (onStepChange) {
      onStepChange(newStep);
    }
  };

  // Call onStepChange on initial render
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStepValue);
    }
  }, []);

  console.log("current step: ", currentStepValue);
  return (
    <div style={{width: '100%'}}>
      <form name={formDefinition.formName} className={formDefinition.formClassName ? formDefinition.formClassName:"modal-input-group"} onSubmit={(e) => e.preventDefault()}>
        {formDefinition.formDefinitionSteps.map((step, stepIndex) => {
          // Use a more stable key if possible, e.g., step.id if it exists and is unique
          const stepKey = `parent-step-${stepIndex}`; // Example: Prefer step.id
          // console.log("{`parent-step-${stepIndex}`}", stepKey);
          return (
            // Removed unnecessary wrapping div
            currentStepValue === stepIndex + 1 && (
              <RenderFormStepContent
                key={stepKey}
                stepIndex={stepIndex}
                step={step}
                submitButtonDefinition={{
                  finalStepCount: formDefinition.formDefinitionSteps.length,
                  currentStepValue: currentStepValue,
                  nextStep: nextStep,
                  prevStep: prevStep,
                  handleSubmitHandler: formDefinition.handleSubmitHandler, // Add this if you have a submit handler
                  handleCancelHandler: formDefinition.handleCancelHandler,
                  customStyle: formDefinition.submitButtonCustomStyle
                }}
              />
            )
          );
        })}
      </form>
    </div>
  );
};

export { FormGenerationService };
